import { FC, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { spaceFileExtension, SpaceKind, SpaceNodeKind } from 'shared';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormInput } from '../../../../components/input/index';
import { ModalWrapper } from '../../../../components/modal-wrapper/index';
import { useLocale, useReactForm } from '../../../../hooks/index';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { useRenameSpaceNode } from '../../hooks/use-rename-space-node.hook';
import { DataRoomModalNode, DataRoomModalStore } from '../../store/data-room-modal.store';
import { TreeSpaceRef } from '../../types/data-room.types';
import { renameSpaceNodeSchema } from '../../validations/rename-space-node.validation';

type RenameSpaceNodeFieldsProps = {
  isOpen: boolean;
  node: DataRoomModalNode;
  onCancel: () => void;
  onRenamed: () => void;
  space: TreeSpaceRef;
};

// Mounted only while the modal is open, so the form starts from the node's current name each time.
// It owns the modal shell too: while a save is pending the modal cannot be dismissed, so a late
// response never closes a different modal opened in the meantime.
const RenameSpaceNodeFields: FC<RenameSpaceNodeFieldsProps> = ({
  isOpen,
  node,
  onCancel,
  onRenamed,
  space,
}) => {
  const { messagesLocale } = useLocale();
  const isFolder = node.kind === SpaceNodeKind.FOLDER;
  const formRef = useRef<HTMLFormElement>(null);
  const schema = useMemo(() => renameSpaceNodeSchema(node), [node]);
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setError,
  } = useReactForm({ defaultValues: { name: node.name }, schema });
  const name = useWatch({ control, name: 'name' });
  const { isPending, renameNode } = useRenameSpaceNode({
    onDuplicateName: () =>
      setError('name', {
        message:
          messagesLocale[isFolder ? 'FOLDER_NAME_ALREADY_EXISTS' : 'FILE_NAME_ALREADY_EXISTS'],
      }),
    onSuccess: onRenamed,
  });
  const isUnchanged = name.trim() === node.name;
  const canSave = isValid && !isUnchanged && !isPending;

  // The dialog focuses the field and selects all of it; a file's extension is not renamed, so the
  // selection stops short of it and typing a new name does not have to retype the extension.
  const selectNameWithoutExtension = (event: Event) => {
    const input = formRef.current?.querySelector('input');

    if (!input) return;

    event.preventDefault();
    input.focus();

    const extension = isFolder ? '' : spaceFileExtension(node.name);

    input.setSelectionRange(
      0,
      extension ? node.name.length - extension.length - 1 : node.name.length,
    );
  };

  const cancel = () => {
    if (!isPending) {
      onCancel();
    }
  };

  const submit = () => {
    void handleSubmit(({ name: newName }) =>
      renameNode({
        id: node.id,
        kind: node.kind,
        name: newName,
        space,
      }),
    )();
  };

  return (
    <ModalWrapper
      className="w-full max-w-[493px] shadow-none"
      control={{ onOpenChange: (open) => !open && cancel(), open: isOpen }}
      onBgClick={cancel}
      onOpenAutoFocus={selectNameWithoutExtension}
      side="CENTER"
    >
      {/* A form, so that Enter in the field saves as the button does, and never while it cannot. */}
      <form
        className="flex w-full flex-col gap-8 rounded bg-white p-4 shadow-3xl"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();

          if (canSave) submit();
        }}
        ref={formRef}
      >
        <div className="flex flex-col gap-4">
          <span className="text-xl font-semibold text-gray-700">
            <AppFormattedMessage id={isFolder ? 'RENAME_FOLDER' : 'RENAME_FILE'} />
          </span>
          <FormInput
            control={control}
            name="name"
            placeholder={<AppFormattedMessage id={isFolder ? 'FOLDER' : 'FILE'} />}
          />
        </div>
        <div className="flex h-11 w-full justify-end gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-300 px-6 py-[10px] text-sm font-[450] text-gray-700"
            disabled={isPending}
            onClick={cancel}
            styleType="NONE"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="h-full w-fit px-6 py-[10px] text-sm font-[550]"
            disabled={!canSave}
            isLoading={isPending}
            type="submit"
          >
            <AppFormattedMessage id="SAVE" />
          </Button>
        </div>
      </form>
    </ModalWrapper>
  );
};

// The one rename of a folder or a file of a workspace or a data room, as the server has one.
export const RenameSpaceNodeModal: FC = () => {
  const isOpen = DataRoomModalStore(({ isOpen }) => isOpen);
  const type = DataRoomModalStore(({ type }) => type);
  const metadata = DataRoomModalStore(({ metadata }) => metadata);
  const handleAccept = DataRoomModalStore(({ handleAccept }) => handleAccept);
  const handleDismiss = DataRoomModalStore(({ handleDismiss }) => handleDismiss);

  const node = metadata?.node;

  // The Vault's files are renamed through the Vault's own modal; only a tree space comes here.
  if (
    type !== DataRoomModalType.RENAME_SPACE_NODE ||
    !metadata ||
    !node ||
    node.spaceKind === SpaceKind.VAULT
  ) {
    return null;
  }

  return (
    <RenameSpaceNodeFields
      isOpen={isOpen}
      key={node.id}
      node={node}
      onCancel={handleDismiss}
      onRenamed={handleAccept}
      space={{ id: metadata.spaceId, kind: node.spaceKind }}
    />
  );
};
